import type { CompanyMembershipRole } from '../../company-context/models/company.models';
import { normalizeCompanyMembershipRole } from '../../company-context/utils/company-membership-role.util';

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function readStr(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function hasTruthyRemovalTimestamp(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return false;
}

/**
 * Indica se o membro tem acesso ativo (projeto/workflow).
 * Inativo explícito (isActive false, removido, timestamps, etc.) → false.
 * Sem sinal claro → ativo (compatível com APIs que só listam membros activos).
 */
export function readMemberAccessActive(
  item: Record<string, unknown>,
  user: Record<string, unknown> = {}
): boolean {
  const removedFlags = [
    item['isRemoved'],
    item['IsRemoved'],
    item['isDeleted'],
    item['IsDeleted'],
    user['isRemoved'],
    user['IsRemoved'],
  ];
  for (const v of removedFlags) {
    if (v === true || v === 1 || v === '1') return false;
    if (v === 'true' || v === 'TRUE') return false;
  }

  const denyAccess = [
    item['hasAccess'],
    item['HasAccess'],
    item['canAccess'],
    item['CanAccess'],
    user['hasAccess'],
    user['HasAccess'],
  ];
  for (const v of denyAccess) {
    if (v === false || v === 0 || v === '0') return false;
    if (v === 'false' || v === 'FALSE') return false;
  }

  const removalTimes = [
    item['removedAt'],
    item['RemovedAt'],
    item['deletedAt'],
    item['DeletedAt'],
    item['leftAt'],
    item['LeftAt'],
    item['deactivatedAt'],
    item['DeactivatedAt'],
    user['removedAt'],
    user['RemovedAt'],
  ];
  for (const t of removalTimes) {
    if (hasTruthyRemovalTimestamp(t)) return false;
  }

  const statusRaw =
    item['status'] ??
    item['Status'] ??
    item['accessStatus'] ??
    item['AccessStatus'] ??
    item['memberStatus'] ??
    item['MemberStatus'] ??
    item['state'] ??
    item['State'] ??
    user['status'] ??
    user['Status'];
  if (statusRaw !== undefined && statusRaw !== null && String(statusRaw).trim() !== '') {
    const s = String(statusRaw).trim().toLowerCase();
    if (
      s === 'inactive' ||
      s === 'inativo' ||
      s === 'disabled' ||
      s === 'desativado' ||
      s === 'revoked' ||
      s === 'removed' ||
      s === 'removido' ||
      s === 'suspended' ||
      s === 'false' ||
      s === '0'
    ) {
      return false;
    }
    if (s === 'active' || s === 'ativo' || s === 'enabled' || s === 'true' || s === '1') {
      return true;
    }
  }
  const candidates = [
    item['isActive'],
    item['IsActive'],
    item['active'],
    item['Active'],
    user['isActive'],
    user['IsActive'],
  ];
  for (const v of candidates) {
    if (typeof v === 'boolean') return v;
    if (v === 0 || v === '0') return false;
    if (v === 1 || v === '1') return true;
    if (v === 'false' || v === 'FALSE') return false;
    if (v === 'true' || v === 'TRUE') return true;
  }
  return true;
}

/** POST /api/projects/{projectId}/members — AssignProjectMemberRequest */
export function buildAssignProjectMemberRequest(userId: string, role: string): Record<string, string> {
  return { userId, role };
}

/**
 * PUT /api/projects/{projectId}/members/{userId} — UpdateProjectMemberRequest.
 * Pelo menos um de `role` (não vazio) ou `isActive` (boolean) é obrigatório.
 */
export function buildUpdateProjectMemberRequest(options: {
  role?: string;
  isActive?: boolean;
}): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (options.role !== undefined && String(options.role).trim() !== '') {
    body['role'] = String(options.role).trim();
  }
  if (options.isActive !== undefined) {
    body['isActive'] = options.isActive;
  }
  return body;
}

export function assertUpdateProjectMemberRequestHasField(body: Record<string, unknown>): void {
  if (!('role' in body) && !('isActive' in body)) {
    throw new Error('UpdateProjectMemberRequest: informe role ou isActive');
  }
}

/** POST /api/workflows/{workflowId}/members — AssignWorkflowMemberRequest */
export function buildAssignWorkflowMemberRequest(userId: string, role: string): Record<string, string> {
  return { userId, role };
}

/** Extrai o userId de um item de `GET /api/projects/:id/members` (várias formas de contrato). */
export function readProjectMemberUserId(
  item: Record<string, unknown>,
  user: Record<string, unknown>
): string {
  const itemKeys = [
    'userId',
    'UserId',
    'applicationUserId',
    'ApplicationUserId',
    'memberUserId',
    'MemberUserId',
  ] as const;
  for (const k of itemKeys) {
    const v = readStr(item[k]);
    if (v) return v;
  }
  const userKeys = ['id', 'Id', 'userId', 'UserId'] as const;
  for (const k of userKeys) {
    const v = readStr(user[k]);
    if (v) return v;
  }
  return readStr(item['id']) || readStr(item['Id']);
}

/**
 * Parse de `GET /api/users` (array directo ou `items`) com papel de membership na empresa.
 */
export function parseCompanyUsersWithMembershipRoles(raw: unknown): Array<{
  id: string;
  name: string;
  email: string;
  companyRole: CompanyMembershipRole;
}> {
  const root = asRecord(raw);
  const arr = Array.isArray(raw) ? raw : asArray(root['items']);
  return arr
    .map((x) => asRecord(x))
    .map((entry) => {
      const id = readStr(entry['id'] ?? entry['Id']);
      const email = readStr(entry['email'] ?? entry['Email']).toLowerCase();
      const name =
        readStr(entry['name'] ?? entry['Name']) ||
        readStr(entry['fullName'] ?? entry['FullName']) ||
        email;
      const companyRole = normalizeCompanyMembershipRole(
        entry['role'] ??
          entry['Role'] ??
          entry['membershipRole'] ??
          entry['MembershipRole'] ??
          entry['companyRole'] ??
          entry['CompanyRole']
      );
      return { id, name, email, companyRole };
    })
    .filter((u) => !!u.id && !!u.email);
}
